import { useSyncStatus } from '../hooks/useSyncStatus'
import { getSyncStatusMeta } from '../services/syncStatusMeta'
import { SyncIssuesPanel } from './SyncIssuesPanel'
import { Button } from '../../../shared/components/ui/button'
import { Badge } from '../../../shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../shared/components/ui/dialog'
import { CloudIcon, CloudOffIcon, RefreshCwIcon, TriangleAlertIcon } from 'lucide-react'

interface SyncStatusDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * SyncStatusDialog component (controlled — triggers live in the account menu
 * on desktop and the mobile nav drawer's sync row)
 * - Shows online/offline status and pending/failed counts
 * - Allows manual sync trigger and per-item retry/discard
 *
 * Keep this mounted (closed) in MainLayout: useSyncStatus registers the
 * app's online/offline listeners and fetches the initial queue stats.
 */
export const SyncStatusDialog = ({ open, onOpenChange }: SyncStatusDialogProps) => {
  const {
    isOnline,
    isSyncing,
    lastSyncTime,
    pendingCount,
    failedCount,
    failedItems,
    error,
    sync,
    clearFailed,
    retryItem,
    discardItem,
    hasPendingItems,
    hasFailedItems,
  } = useSyncStatus()

  const statusMeta = getSyncStatusMeta({ isOnline, isSyncing, pendingCount, failedCount })

  // Calculate last sync text - Date.now() is intentionally called during render
  // to show real-time relative timestamps

  const getLastSyncText = () => {
    if (!lastSyncTime) return 'Never synced'
    // eslint-disable-next-line react-hooks/purity
    const diff = Date.now() - lastSyncTime
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return 'Just now'
    if (minutes === 1) return '1 minute ago'
    if (minutes < 60) return `${minutes} minutes ago`
    const hours = Math.floor(minutes / 60)
    if (hours === 1) return '1 hour ago'
    return `${hours} hours ago`
  }
  const lastSyncText = getLastSyncText()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sync Status</DialogTitle>
          <DialogDescription>View and manage offline data synchronization</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status overview */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <p className="text-sm font-medium">Connection Status</p>
              <p className={`text-xs ${statusMeta.colorClass}`}>
                {isOnline ? 'Online' : 'Offline - Changes will sync when connected'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isOnline ? (
                <CloudIcon className="h-6 w-6 text-success" />
              ) : (
                <CloudOffIcon className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Sync stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border p-3 text-center">
              <p className="text-2xl font-bold">{pendingCount}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="text-2xl font-bold">{failedCount}</p>
              <p className="text-xs text-muted-foreground">Failed</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="text-2xl font-bold">{isSyncing ? '...' : '✓'}</p>
              <p className="text-xs text-muted-foreground">Status</p>
            </div>
          </div>

          {/* Last sync time */}
          <div className="rounded-lg bg-muted p-3">
            <p className="text-xs text-muted-foreground">Last sync: {lastSyncText}</p>
          </div>

          {/* Dead-lettered changes with per-item retry/discard */}
          <SyncIssuesPanel
            items={failedItems}
            isOnline={isOnline}
            onRetry={(id) => void retryItem(id)}
            onDiscard={(id) => void discardItem(id)}
          />

          {/* Error message */}
          {error && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-3">
              <div className="flex items-start gap-2">
                <TriangleAlertIcon className="h-4 w-4 text-destructive mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-destructive">Sync Error</p>
                  <p className="text-xs text-destructive/80 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={() => void sync()}
              disabled={!isOnline || isSyncing}
              className="flex-1"
            >
              {isSyncing ? (
                <>
                  <RefreshCwIcon className="mr-2 h-4 w-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCwIcon className="mr-2 h-4 w-4" />
                  Sync Now
                </>
              )}
            </Button>

            {hasFailedItems && (
              <Button onClick={() => void clearFailed()} variant="outline" className="flex-1">
                Discard All Failed
              </Button>
            )}
          </div>

          {/* Info badges */}
          <div className="flex flex-wrap gap-2">
            {!isOnline && (
              <Badge variant="secondary">
                <CloudOffIcon className="mr-1 h-3 w-3" />
                Working Offline
              </Badge>
            )}
            {hasPendingItems && <Badge variant="secondary">{pendingCount} items queued</Badge>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
